import React, { useState, useEffect } from 'react';
import axiosInstance from '@/lib/axios';
import axios from 'axios';
import { 
  Gift, Share2, Copy, Check, Users, Twitter, Facebook, 
  MessageCircle, Mail, Link2, Sparkles, Trophy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Referral Module Component
 * Allows users to share their referral code and track referrals
 */
export default function ReferralModule({ user }) {
  const [referralCode, setReferralCode] = useState('');
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const response = await axiosInstance.get(`${API}/referral/my-code`, { withCredentials: true });
      setReferralCode(response.data.referral_code);
      setReferralStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    const shareUrl = `${window.location.origin}?ref=${referralCode}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Lien copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Impossible de copier');
    }
  };

  const shareUrl = `${window.location.origin}?ref=${referralCode}`;
  const shareText = "J'utilise Achat Immo Gagnant pour mes simulations immobilières. Inscris-toi avec mon lien et obtiens 1 simulation gratuite !";

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
    email: `mailto:?subject=${encodeURIComponent("Simulation immobilière gratuite !")}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Achat Immo Gagnant',
          text: shareText,
          url: shareUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      copyToClipboard();
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 animate-pulse">
        <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
        <div className="h-20 bg-slate-100 rounded"></div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 shadow-sm border border-purple-200">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
          <Gift className="h-6 w-6 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-slate-900">Parrainez vos amis</h3>
          <p className="text-sm text-slate-600">1 simulation offerte pour chaque filleul !</p>
        </div>
      </div>

      {/* Stats */}
      {referralStats && (
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-white/80 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-purple-600">{referralStats.total_referrals || 0}</div>
            <div className="text-xs text-slate-500">Filleuls</div>
          </div>
          <div className="bg-white/80 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-green-600">{referralStats.simulations_earned || 0}</div>
            <div className="text-xs text-slate-500">Simus gagnées</div>
          </div>
          <div className="bg-white/80 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-amber-600">{referralStats.pending_referrals || 0}</div>
            <div className="text-xs text-slate-500">En attente</div>
          </div>
        </div>
      )}

      {/* Referral Code Box */}
      <div className="bg-white rounded-xl p-4 mb-4 border border-purple-100">
        <label className="text-xs font-medium text-slate-500 mb-1 block">Votre code de parrainage</label>
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-slate-50 rounded-lg px-4 py-3 font-mono text-lg font-bold text-purple-700 tracking-wider">
            {referralCode}
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={copyToClipboard}
            className="h-12 w-12 border-purple-200 hover:bg-purple-50"
          >
            {copied ? (
              <Check className="h-5 w-5 text-green-500" />
            ) : (
              <Copy className="h-5 w-5 text-purple-500" />
            )}
          </Button>
        </div>
      </div>

      {/* Share Link */}
      <div className="mb-4">
        <label className="text-xs font-medium text-slate-500 mb-1 block">Lien de partage</label>
        <div className="flex items-center gap-2">
          <Input 
            readOnly 
            value={shareUrl}
            className="text-sm bg-white/80 border-purple-100"
          />
          <Button
            onClick={handleNativeShare}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Partager
          </Button>
        </div>
      </div>

      {/* Social Share Buttons */}
      <div className="flex items-center justify-center gap-3">
        <span className="text-xs text-slate-500">Partager sur :</span>
        <a 
          href={shareLinks.twitter}
          target="_blank"
          rel="noopener noreferrer"
          className="w-10 h-10 bg-[#1DA1F2] hover:bg-[#1a8cd8] rounded-full flex items-center justify-center transition-colors"
        >
          <Twitter className="h-5 w-5 text-white" />
        </a>
        <a 
          href={shareLinks.facebook}
          target="_blank"
          rel="noopener noreferrer"
          className="w-10 h-10 bg-[#1877F2] hover:bg-[#166fe5] rounded-full flex items-center justify-center transition-colors"
        >
          <Facebook className="h-5 w-5 text-white" />
        </a>
        <a 
          href={shareLinks.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          className="w-10 h-10 bg-[#25D366] hover:bg-[#20bd5a] rounded-full flex items-center justify-center transition-colors"
        >
          <MessageCircle className="h-5 w-5 text-white" />
        </a>
        <a 
          href={shareLinks.email}
          className="w-10 h-10 bg-slate-600 hover:bg-slate-700 rounded-full flex items-center justify-center transition-colors"
        >
          <Mail className="h-5 w-5 text-white" />
        </a>
      </div>

      {/* How it works */}
      <div className="mt-5 pt-4 border-t border-purple-200">
        <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-purple-500" />
          Comment ça marche ?
        </h4>
        <ol className="text-xs text-slate-600 space-y-1">
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold shrink-0">1</span>
            Partagez votre lien sur les réseaux sociaux
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold shrink-0">2</span>
            Votre ami s'inscrit avec votre code
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold shrink-0">3</span>
            Vous recevez tous les deux 1 simulation gratuite !
          </li>
        </ol>
      </div>
    </div>
  );
}
